import CustomDialog from 'Components/CustomDialog/CustomDialog'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import { useRequestWalttiPosOrderRefundMutation } from 'Services/SalesManagement/Features/Order/orderApi'
import type {
  AffiliateOrderRefundOptions,
  WalttiPosOrderRefundRequest
} from 'Services/SalesManagement/Features/Order/types'
import type { FieldValidationErrors } from 'Services/SalesManagement/types'
import useTypedSelector from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { type FormEvent, useState } from 'react'
import { t } from 'ttag'

interface WalttiPosOrderRefundDialogProps {
  orderId: number
  open: boolean
  walttiPosOrderRefundOptions: { field_choices: AffiliateOrderRefundOptions } | undefined
  isRefundOptionsLoading: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function WalttiPosOrderRefundDialog({
  orderId,
  open,
  walttiPosOrderRefundOptions,
  isRefundOptionsLoading,
  onClose,
  onSuccess
}: Readonly<WalttiPosOrderRefundDialogProps>) {
  const { user } = useTypedSelector(state => state.user)
  const [refundData, setRefundData] = useState<WalttiPosOrderRefundRequest>({
    id: orderId,
    reason: '',
    note: '',
    requester_email: user?.email ?? '',
    requester_full_name: ''
  })

  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({})
  const [requestWalttiPosOrderRefund, { isLoading: isRefundLoading }] =
    useRequestWalttiPosOrderRefundMutation()

  const refundReasons = walttiPosOrderRefundOptions?.field_choices?.reason || []

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleRequestRefund()
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    field: keyof WalttiPosOrderRefundRequest
  ) => {
    setRefundData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    setFieldErrors(prevState => ({ ...prevState, [field]: [] }))
  }

  const handleRequestRefund = () => {
    requestWalttiPosOrderRefund(refundData)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Refund successfully requested.`)
        onClose()
        onSuccess()
      })
      .catch(err => {
        const validationErrors = err?.data as FieldValidationErrors | undefined
        // If there are validation errors for fields that are in refundData, set them
        if (Object.keys(validationErrors ?? {}).some(key => key in refundData)) {
          setFieldErrors(prev => ({ ...prev, ...validationErrors }))
        } else {
          SnackbarUtils.error(err?.data.error ?? t`There was an error requesting the refund.`)
        }
      })
  }

  return (
    <CustomDialog open={open} handleClose={onClose}>
      <DialogTitle id="dialog-title">{t`Request refund`}</DialogTitle>
      <StyledForm onSubmit={onSubmit} isDisabled={isRefundLoading}>
        <WithLoader loading={isRefundOptionsLoading} rows={4}>
          <DialogContent>
            <Box>
              <DialogContentText sx={{ pb: 1 }}>
                {t`You are about to request a refund for the whole order.`}
              </DialogContentText>
              <FormControl sx={{ minWidth: 200 }} size="small" required>
                <InputLabel>{t`Refund reason`}</InputLabel>
                <Select
                  required
                  value={refundData.reason}
                  label={t`Refund reason`}
                  autoWidth
                  onChange={event => handleChange(event, 'reason')}
                  error={fieldErrors.reason?.length > 0}
                >
                  {refundReasons.map((option: [number, string]) => (
                    <MenuItem key={option[0]} value={option[0]}>
                      {option[1]}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.reason?.length > 0}>
                  {fieldErrors.reason?.length > 0 ? fieldErrors.reason[0] : ''}
                </FormHelperText>
              </FormControl>
              <TextField
                sx={{ minWidth: 380 }}
                required
                label={t`Refund requester's full name`}
                name="refund_requester_name"
                size="small"
                placeholder={t`Full name of the person requesting the refund`}
                value={refundData.requester_full_name}
                onChange={event => handleChange(event, 'requester_full_name')}
                error={fieldErrors.requester_full_name?.length > 0}
                helperText={
                  fieldErrors.requester_full_name?.length > 0
                    ? fieldErrors.requester_full_name[0]
                    : ''
                }
              />
              <TextField
                sx={{ width: '100%' }}
                label={t`Note`}
                multiline
                rows={3}
                value={refundData.note}
                onChange={event => handleChange(event, 'note')}
                error={fieldErrors.note?.length > 0}
                helperText={fieldErrors.note?.length > 0 ? fieldErrors.note[0] : ''}
              />
            </Box>
          </DialogContent>
        </WithLoader>
        <DialogActions>
          <Button sx={{ color: 'text.primary' }} onClick={onClose}>{t`Cancel`}</Button>
          <CustomLoadingButton
            isLoading={isRefundLoading}
            type="submit"
            size="medium"
            iconPosition="end"
            autoFocus
          >
            {t`Send refund request`}
          </CustomLoadingButton>
        </DialogActions>
      </StyledForm>
    </CustomDialog>
  )
}
